import { UserCheck2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { tv } from "tailwind-variants";

import { ROUTES } from "@/router";
import { useMultiStepFormStore } from "@/stores";
import { Button } from "@/ui";
import { formatDate, PATIENT_STATUS } from "@/utils";
import type { PatientCaseStatus } from "@/utils";

export const edgeCaseVariants = tv({
  slots: {
    externalCircle: "absolute size-24 rounded-full",
    internalCircle: "absolute size-20 rounded-full",
    icon: "z-10 rounded-full",
    statusPill: "rounded-full px-3 py-1 capitalize",
    messageBox: "rounded-xl p-4 border border",
  },
  variants: {
    status: {
      [PATIENT_STATUS.ACTIVE]: {
        externalCircle: "bg-success-50",
        internalCircle: "bg-success-100",
        icon: "bg-success-200 text-success-900",
        statusPill: "text-success-950 bg-success-50",
        messageBox: "bg-success-50 text-success-900",
      },
      [PATIENT_STATUS.DISQUALIFIED]: {
        externalCircle: "bg-error-50",
        internalCircle: "bg-error-100",
        icon: "bg-error-100 text-error-700",
        statusPill: "text-error-700 bg-error-100",
        messageBox: "bg-error-50 text-error-950 border-error-700",
      },
      [PATIENT_STATUS.TRANSFERRED]: {
        externalCircle: "bg-warning-50",
        internalCircle: "bg-warning-100",
        icon: "bg-warning-100 text-warning-900",
        statusPill: "text-warning-950 bg-warning-50",
        messageBox: "bg-warning-50 text-warning-950 border-warning-600",
      },
    },
  },
  defaultVariants: {
    status: PATIENT_STATUS.ACTIVE,
  },
});

interface EdgeCaseProps {
  status: Exclude<PatientCaseStatus, "new_patient">;
  description: string;
}

export const EdgeCase = ({ status, description }: EdgeCaseProps) => {
  const navigate = useNavigate();

  const { multiStepFormData, goToNextFormStep } = useMultiStepFormStore();
  const generalInformationFormData =
    multiStepFormData?.generalInformationFormData;

  const { externalCircle, internalCircle, icon, statusPill, messageBox } =
    edgeCaseVariants({
      status,
    });

  if (!generalInformationFormData) {
    return null;
  }
  return (
    <div className="flex h-full flex-col items-center justify-center gap-10">
      <div className="relative flex items-center justify-center pb-6">
        <div className={externalCircle()}></div>
        <div className={internalCircle()}></div>
        <UserCheck2 className={icon()} size={30} />
      </div>
      <div className="flex w-3/5 flex-col gap-y-3">
        <div className="flex justify-between rounded-xl border border-neutral-50 bg-white p-4">
          <p className="content-center font-medium text-primary-950">
            {`${generalInformationFormData.firstName} ${generalInformationFormData.lastName}`}
          </p>
          <p className="content-center text-sm text-neutral-500">
            {formatDate(generalInformationFormData.dateOfBirth)}
          </p>
          <p className="content-center text-sm text-neutral-500">
            {generalInformationFormData.phoneNumber}
          </p>
          <p className="content-center text-sm text-neutral-500">
            {generalInformationFormData.email}
          </p>
          <p className={statusPill()}>{status}</p>
        </div>
        <div className={messageBox()}>
          <p>{description}</p>
        </div>
      </div>
      {status === PATIENT_STATUS.DISQUALIFIED && (
        <div className="flex flex-col items-center gap-y-3">
          <p className="text-neutral-500">
            If the reason for disqualification has changed, you may proceed with
            submitting the form.
          </p>
          <Button
            className="w-fit"
            onClick={() => {
              goToNextFormStep();
              void navigate(ROUTES.newIntake);
            }}
          >
            Continue anyways
          </Button>
        </div>
      )}
    </div>
  );
};
